import { Controller, useFormContext } from "react-hook-form";
import { Box, Chip, FormControl, FormHelperText, MenuItem, Select, } from "@mui/material";
import { ZyusetuFieldKeyFragmentFragment, ZyusetuFieldKeyType, } from "@/graphql/graphql";
import { FormSchema } from "@/components/pages/Zyusetu/ZyusetuEditorSection.tsx";
import { zyusetuValueIntoZodValue } from "@/hooks/useGetFirstContent";

interface ZyusetuSelectManyFieldProps {
  fieldKey: ZyusetuFieldKeyFragmentFragment;
  onFocus: () => void;
  updateField: (
    keyId: string,
    keyName: string,
    value: FormSchema<ZyusetuFieldKeyType.SelectMany>[string],
  ) => Promise<void>;
  isAvailable: boolean;
}

export function ZyusetuSelectManyField({
  fieldKey,
  isAvailable,
  onFocus,
  updateField,
}: ZyusetuSelectManyFieldProps) {
  const { control } =
    useFormContext<FormSchema<ZyusetuFieldKeyType.SelectMany>>();
  return (
    <Controller
      name={fieldKey.keyName}
      control={control}
      render={({ field, formState: { errors }, fieldState: { isDirty } }) => (
        <FormControl
          fullWidth
          error={!!errors?.[fieldKey.keyName]?.value?.message}
        >
          <Select
            inputRef={field.ref}
            onFocus={onFocus}
            {...field}
            disabled={!isAvailable}
            value={
              Array.isArray(field.value.value)
                ? field.value.value
                : [field.value.value]
            }
            size={"small"}
            multiple={true}
            onBlur={async () => {
              if (errors?.[fieldKey.keyName]?.value?.message) return;
              if (!isDirty) return;

              await updateField(fieldKey.id, fieldKey.keyName, field.value);
            }}
            onChange={(e) => {
              field.onChange(
                zyusetuValueIntoZodValue(fieldKey, e.target.value ?? []),
              );
            }}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    tabIndex={-1}
                    size={"small"}
                    key={value}
                    label={value}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onDelete={async () => {
                      await updateField(
                        fieldKey.id,
                        fieldKey.keyName,
                        field.value,
                      );
                      field.onChange(
                        zyusetuValueIntoZodValue(
                          fieldKey,
                          selected.filter((v) => v !== value),
                        ),
                      );
                    }}
                  />
                ))}
              </Box>
            )}
          >
            {fieldKey.items?.map((option) => (
              <MenuItem key={`${fieldKey.id}-select-${option}`} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
          {errors?.[fieldKey.keyName]?.value?.message &&
            <FormHelperText style={{ color: "red" }}>
              {errors?.[fieldKey.keyName]?.value?.message || ""}
            </FormHelperText>}
        </FormControl>
      )}
    />
  );
}
